import React from 'react';
import {Link, useLocation, useParams} from 'react-router-dom';

import CalendarMonthChart from '../../components/eval/CalendarMonthChart';
import LeadHorizonChart from '../../components/eval/LeadHorizonChart';
import LocationMiniMap from '../../components/eval/LocationMiniMap';
import ModelComparisonTable, {ModelResult} from '../../components/eval/ModelComparisonTable';
import MonthHorizonHeatmap from '../../components/eval/MonthHorizonHeatmap';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageContainer from '../../components/PageContainer';
import {useSiteSummary} from '../../hooks/eval/useSiteSummary';
import {SeasonalViews, SiteSummary, WindowMetrics} from '../../lib/api/eval';
import {getModelColor, getModelDisplayName} from '../../lib/evalModels';

type Params = {siteId: string};

type SeasonalMetricKey = 'kge' | 'nrmse' | 'mae' | 'percentBias';

const METRIC_OPTIONS: Array<{key: SeasonalMetricKey; label: string}> = [
  {key: 'kge', label: 'KGE'},
  {key: 'nrmse', label: 'nRMSE'},
  {key: 'mae', label: 'MAE'},
  {key: 'percentBias', label: '%Bias'},
];

const SPRING_MELT_MONTHS = new Set([4, 5, 6, 7]);

function getSeasonalViews(summary: SiteSummary): SeasonalViews | undefined {
  // Look for the seasonal config in hydroforecast model
  const hfModel = summary.models?.hydroforecast;
  if (!hfModel?.configs) return undefined;
  const seasonalConfig = hfModel.configs.seasonal;
  return seasonalConfig?.seasonalViews;
}

function getOrderedModelNames(models: Record<string, unknown>): string[] {
  const names = Object.keys(models);
  const orderKey = (m: string) => {
    const key = m.toLowerCase();
    if (key === 'hydroforecast') return 0;
    if (key === 'persistence' || key === 'lta') return 9;
    return 1;
  };
  return names.sort((a, b) => orderKey(a) - orderKey(b) || a.localeCompare(b));
}

function MetricCard({
  label,
  value,
  format,
}: {
  label: string;
  value: number | null | undefined;
  format?: (v: number) => string;
}) {
  const fmt = format ?? ((v: number) => v.toFixed(2));
  const display = value != null && Number.isFinite(value) ? fmt(value) : '--';

  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-3 text-center">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{display}</div>
    </div>
  );
}

export default function SeasonalSiteDetail() {
  const {siteId} = useParams() as Params;
  const {pathname} = useLocation();
  const {summary, isLoaded} = useSiteSummary(siteId);
  const [metric, setMetric] = React.useState<SeasonalMetricKey>('kge');

  const hasShortTerm = summary
    ? Object.values(summary.models).some((m) => Object.keys(m.windows || {}).length > 0)
    : false;

  const seasonalViews = summary ? getSeasonalViews(summary) : undefined;

  const BASELINE_MODELS = new Set(['persistence', 'lta']);
  const modelNames = summary
    ? getOrderedModelNames(summary.models).filter(
        (m) => !BASELINE_MODELS.has(m.toLowerCase()) && !!summary.models[m]?.configs?.seasonal
      )
    : [];

  // Build model comparison results from the seasonal config's 90d window
  const modelResults: ModelResult[] = React.useMemo(() => {
    if (!summary) return [];
    const allModelNames = getOrderedModelNames(summary.models);
    const persistenceW = summary.models.persistence?.configs?.seasonal?.windows?.['90d'];
    const ltaW = summary.models.lta?.configs?.seasonal?.windows?.['90d'];

    const nonBaselineResults: ModelResult[] = allModelNames
      .filter((m) => !BASELINE_MODELS.has(m.toLowerCase()))
      .map((model) => {
        const windowData: WindowMetrics | undefined =
          summary.models[model]?.configs?.seasonal?.windows?.['90d'];
        if (!windowData) return null;
        return {
          model,
          metrics: {
            kge: windowData.kge?.mean,
            crps: windowData.crps?.mean,
            nrmse: windowData.nrmse?.mean,
            mae: windowData.mae?.mean,
            coverage80: windowData.coverage80?.mean,
            percentBias: windowData.percentBias?.mean,
            kgePersistence: persistenceW?.kge?.mean,
            kgeLta: ltaW?.kge?.mean,
            nrmsePersistence: persistenceW?.nrmse?.mean,
            nrmseLta: ltaW?.nrmse?.mean,
            maePersistence: persistenceW?.mae?.mean,
            maeLta: ltaW?.mae?.mean,
          },
        } as ModelResult;
      })
      .filter((r): r is ModelResult => r !== null);

    const baselineResults: ModelResult[] = ['persistence', 'lta']
      .map((model) => {
        const windowData: WindowMetrics | undefined =
          summary.models[model]?.configs?.seasonal?.windows?.['90d'];
        if (!windowData) return null;
        return {
          model,
          isBaseline: true,
          metrics: {
            kge: windowData.kge?.mean,
            crps: windowData.crps?.mean,
            nrmse: windowData.nrmse?.mean,
            mae: windowData.mae?.mean,
            percentBias: windowData.percentBias?.mean,
          },
        } as ModelResult;
      })
      .filter((r): r is ModelResult => r !== null);

    return [...nonBaselineResults, ...baselineResults];
  }, [summary]);

  return (
    <PageContainer>
      <div className="mb-4">
        <Link to="/eval/seasonal" className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; Back to seasonal overview
        </Link>
      </div>

      {hasShortTerm && (
        <div className="flex gap-1 mb-4 border-b border-gray-200">
          <Link
            to={pathname}
            className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 -mb-px"
          >
            Short-Term (10d)
          </Link>
          <span className="px-3 py-2 text-sm font-medium border-b-2 border-blue-500 text-blue-600 -mb-px">
            Seasonal (365d)
          </span>
        </div>
      )}

      {!isLoaded ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : !summary ? (
        <div className="py-6 text-center text-gray-500">Site not found or still loading...</div>
      ) : (
        <>
          {/* Header */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="order-2 md:order-1">
              {summary.lat != null && summary.lon != null ? (
                <LocationMiniMap lat={summary.lat} lon={summary.lon} height={200} />
              ) : (
                <div className="h-[200px] border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-500">
                  Location not available
                </div>
              )}
            </div>
            <div className="order-1 md:order-2">
              <h1 className="text-2xl font-semibold text-gray-900 mb-4">
                {summary.displayName || siteId}
              </h1>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <th className="py-2 pr-4 text-left text-gray-500 font-normal w-1/3">Site ID</th>
                    <td className="py-2 font-medium">
                      {summary.organizationId && summary.projectId ? (
                        <Link
                          to={`/${summary.organizationId}/projects/${summary.projectId}?siteId=${siteId}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {siteId}
                        </Link>
                      ) : (
                        siteId
                      )}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <th className="py-2 pr-4 text-left text-gray-500 font-normal">Product</th>
                    <td className="py-2 font-medium">Seasonal (365-day)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <th className="py-2 pr-4 text-left text-gray-500 font-normal">Models</th>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-2">
                        {modelNames.map((model) => (
                          <span key={model} className="inline-flex items-center gap-1.5">
                            <span
                              className="w-2.5 h-2.5 rounded-full"
                              style={{backgroundColor: getModelColor(model)}}
                            />
                            <span className="font-medium">
                              {getModelDisplayName(model, 'seasonal')}
                            </span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Metric selector */}
          <div className="mb-6 flex items-center gap-3">
            <span className="text-sm text-gray-600">Metric:</span>
            <div className="flex gap-1">
              {METRIC_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    metric === opt.key
                      ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setMetric(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Section 1: Calendar Month Performance */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-200">
              Performance by Calendar Month
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              How does the model perform across different months and flow regimes? Spring melt
              months (Apr-Jul) are highlighted.
            </p>
            <div className="border border-gray-200 bg-white p-4 rounded-md">
              <CalendarMonthChart
                data={seasonalViews?.byValidMonth}
                metric={metric}
                highlightMonths={SPRING_MELT_MONTHS}
              />
            </div>
          </section>

          {/* Section 2: Spring Melt Aggregate */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-200">
              Spring Melt Aggregate (Apr-Jul)
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Pooled performance over the spring melt season — the key metric for reservoir storage
              customers.
            </p>
            {seasonalViews?.springMelt && (seasonalViews.springMelt.forecastCount ?? 0) > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="KGE" value={seasonalViews.springMelt.kge} />
                  <MetricCard label="nRMSE" value={seasonalViews.springMelt.nrmse} />
                  <MetricCard label="MAE" value={seasonalViews.springMelt.mae} />
                  <MetricCard
                    label="%Bias"
                    value={seasonalViews.springMelt.percentBias}
                    format={(v) => `${v.toFixed(1)}%`}
                  />
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  Based on {seasonalViews.springMelt.forecastCount} forecast-hour pairs
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-400 py-4 text-center">
                No forecast valid times fall within Apr–Jul for this site
              </div>
            )}
          </section>

          {/* Section 3: Lead-Time Skill Curve */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-200">
              Lead-Time Skill Curve
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              How accuracy changes with forecast horizon. Each point pools all forecasts within that
              lead time window.
            </p>
            <div className="border border-gray-200 bg-white p-4 rounded-md">
              <LeadHorizonChart data={seasonalViews?.byLeadHorizon} metric={metric} />
            </div>
          </section>

          {/* Section 4: Monthly Volume at Horizon */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-200">
              Monthly Volume Accuracy by Lead Time
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              How accurate was the volume prediction for each target month at different lead times?
              Cells with fewer than 3 data points are grayed out.
            </p>
            <div className="border border-gray-200 bg-white p-4 rounded-md">
              <MonthHorizonHeatmap data={seasonalViews?.monthlyVolumeByHorizon} metric={metric} />
            </div>
          </section>

          {/* Section 5: Model Comparison Table */}
          {modelResults.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-200">
                Model Comparison (90-day window)
              </h2>
              <ModelComparisonTable results={modelResults} product="seasonal" />
            </section>
          )}
        </>
      )}
    </PageContainer>
  );
}
